import React, { useState, useContext } from "react"
import PropTypes from "prop-types"
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import ProjectContext from "context/projects/projectContext"
import i18n from "i18n"
import OtherFormatsDropzone from "components/Dropzone/OtherFormatsDropzone"

const UploadOtherFormatsModal = ({
  openModal,
  setOpenModal,
  proyecto,
  variant,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([])
  const projectContext = useContext(ProjectContext)
  const { uploadProjectFromOtherFormats } = projectContext
  const [loadingBtn, setLoadingBtn] = useState(false)
  const isPDFAI = variant === "pdfAI"

  const onUpload = async () => {
    setLoadingBtn(true)
    await uploadProjectFromOtherFormats(selectedFiles, proyecto.organizationId)
    setOpenModal(false)
    setLoadingBtn(false)
  }

  return (
    <Modal
      open={openModal}
      onClose={() => {
        null
      }}
    >
      <Box
        data-testid="project-other-formats-upload-modal"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isPDFAI ? 600 : 550,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "calc(100vh - 64px)",
          overflowY: "auto",
          overflowX: "hidden",
          bgcolor: "background.paper",
          boxShadow: 5,
          borderRadius: "6px",
          px: isPDFAI ? 5 : 4,
          pt: isPDFAI ? 5 : 4,
          pb: 4,
        }}
      >
        <IconButton
          data-testid="project-other-formats-upload-modal-close"
          onClick={() => {
            setOpenModal(false)
          }}
          aria-label="Close"
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ textAlign: "center" }}>
          {!isPDFAI && (
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: 22,
                lineHeight: "23px",
                color: "#414141",
                mb: 3,
              }}
            >
              {i18n.t("¡Importante!")}
            </Typography>
          )}

          <Typography
            sx={{
              fontWeight: 700,
              lineHeight: isPDFAI ? "26px" : "18px",
              fontSize: isPDFAI ? 20 : 14,
              color: "#414141",
              maxWidth: isPDFAI ? 560 : "none",
              mx: "auto",
              mb: isPDFAI ? 3 : 2,
            }}
          >
            {isPDFAI
              ? "Sube aquí tu archivo PDF para convertirlo a presupuesto en Planhopper."
              : "Sube aquí archivos Excel, PDF, CSV, TXT, DOC, CAD o Revit."}
          </Typography>

          <Typography
            sx={{
              fontWeight: 500,
              lineHeight: "24px",
              fontSize: 16,
              color: "#414141",
              maxWidth: isPDFAI ? 460 : 390,
              mx: "auto",
              mb: isPDFAI ? 3 : 4,
            }}
          >
            {isPDFAI
              ? "Una vez lo recibamos, revisaremos si se puede procesar correctamente y te contactaremos con la confirmación."
              : "Una vez lo hayamos recibido, lo subiremos a tu cuenta y recibirás un correo cuando esté disponible."}
          </Typography>

          <Typography
            sx={{
              fontWeight: 500,
              lineHeight: "24px",
              fontSize: 16,
              color: "#414141",
              maxWidth: isPDFAI ? 520 : 390,
              mx: "auto",
              mb: 4,
            }}
          >
            {isPDFAI
              ? "Si el PDF es viable, te pasaremos rápidamente un precio por partida. También podrás consultar packs con descuento."
              : "Si tienes alguna duda, nos puedes contactar por WhatsApp desde el menú lateral izquierdo o por correo a soporte@planhopper.com"}
          </Typography>
        </Box>

        <Box sx={{ px: isPDFAI ? 0.5 : 5 }}>
          <OtherFormatsDropzone
            selectedFiles={selectedFiles}
            setselectedFiles={setSelectedFiles}
            acceptedExtensions={isPDFAI ? [".pdf"] : undefined}
            maxFiles={isPDFAI ? 1 : undefined}
            title={
              isPDFAI ? "Arrastra tu PDF o explora tu ordenador" : undefined
            }
            description={isPDFAI ? "Puedes cargar archivos .pdf" : undefined}
            titleFontWeight={isPDFAI ? "700" : undefined}
            dropzoneClassName={isPDFAI ? "" : undefined}
            dropzoneStyle={
              isPDFAI
                ? {
                    minHeight: 250,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "32px",
                  }
                : undefined
            }
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button
            data-testid={
              selectedFiles.length == 0 || loadingBtn
                ? "project-other-formats-upload-submit-disabled"
                : "project-other-formats-upload-submit-button"
            }
            data-amplify-analytics-on="click"
            data-amplify-analytics-name="ExcelUploaded"
            data-amplify-analytics-attrs={selectedFiles}
            type="button"
            variant="contained"
            disabled={selectedFiles.length == 0 || loadingBtn}
            onClick={onUpload}
            color="branded"
            size="large"
            sx={{
              gap: 2,
              "&.Mui-disabled": {
                color: "#4f5660",
              },
            }}
          >
            {i18n.t("Subir")}
            {loadingBtn && (
              <CircularProgress size={20} sx={{ color: "black" }} />
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

UploadOtherFormatsModal.propTypes = {
  openModal: PropTypes.bool.isRequired,
  setOpenModal: PropTypes.func.isRequired,
  proyecto: PropTypes.object,
  variant: PropTypes.string,
}

export default UploadOtherFormatsModal
